import { Button, Divider, FormControl, FormHelperText, FormLabel, Grid, Input, Stack, Typography } from '@mui/joy';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ButtonLink } from '@/components/router/ButtonLink';
import { RouterLink } from '@/components/router/RouterLink';
import { I18N_DE } from '@/i18n/de';
import { EMAIL_PATTERN } from '@/modules/EMAIL_PATTERN';
import { UpdateUserDto, User, UserRole, UserStatus } from '@/modules/api';
import { backendApiUsersFindOneOptions, backendApiUsersUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { EntityActive } from '@/types/EntityActive';

export const Route = createFileRoute('/_authenticated/_management/administration/benutzerinnen/$userId')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: UserDetailsRoute,
});

type FormData = Pick<UpdateUserDto, 'role' | 'status' | 'email'>;

const compareUserData = (user: User, formData: FormData) =>
  formData.role === user.role && formData.status === user.status && formData.email === user.email;

function UserDetailsRoute() {
  const { userId } = Route.useParams();

  const { data: user, error } = useQuery(backendApiUsersFindOneOptions({ path: { userId } }));

  const { mutate, isPending } = useMutation(backendApiUsersUpdateMutation());

  const {
    control,
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        role: user.role,
        status: user.status,
      });
    }
  }, [user, reset]);

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    user && (
      <Stack>
        <Grid
          container
          spacing={2}
          component="form"
          onSubmit={handleSubmit(async (data) => {
            if (isPending) return;

            mutate({ path: { userId: user.id }, body: data });
          })}
        >
          <Grid md={12}>
            <Typography level="h3">Profil</Typography>

            <Typography color="neutral">{`Registriert über Service BW mit E-Mail: ${user.registrationEmail}`}</Typography>
          </Grid>
          <Grid md={6}>
            <FormControl>
              <FormLabel>Vorname</FormLabel>
              <Input value={user.firstName} readOnly />
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl>
              <FormLabel>Nachname</FormLabel>
              <Input value={user.lastName} readOnly />
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl error={!!errors.email}>
              <FormLabel>Dienstliche E-Mail-Adresse</FormLabel>
              <Input {...register('email', { required: true, pattern: EMAIL_PATTERN })} variant="outlined" />
              {errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl>
              <FormLabel>Telefonnummer</FormLabel>
              <Input value={user.phoneNumber} readOnly />
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl>
              <FormLabel>Institution</FormLabel>
              <Input value={I18N_DE.UserInstitutionType[user.institutionType]} readOnly />
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl>
              <FormLabel>Funktion</FormLabel>
              <Input value={user.roleText} readOnly />
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl>
              <FormLabel>Rolle</FormLabel>
              <Controller
                control={control}
                name="role"
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    options={Object.values(UserRole)}
                    optionLabel={getI18nRadioLabel({
                      i18nTitle: I18N_DE.UserRole,
                      i18nDescription: I18N_DE.UserRoleDescription,
                    })}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid md={6}>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    options={Object.values(UserStatus)}
                    disabledOptions={user?.status !== UserStatus.REGISTERED ? [UserStatus.REGISTERED] : undefined}
                    optionLabel={getI18nRadioLabel({
                      i18nTitle: I18N_DE.UserStatus,
                      i18nDescription: I18N_DE.UserStatusDescription,
                    })}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid md={12}>
            <Stack marginTop={4} direction="row" justifyContent="flex-end">
              <Button type="submit" disabled={compareUserData(user, watch()) || isPending} loading={isPending}>
                Speichern
              </Button>
            </Stack>
          </Grid>
        </Grid>
        <Divider sx={{ marginBottom: 2, marginTop: 2 }} />
        <Grid container spacing={2}>
          <Grid md={12}>
            <Typography level="h3" marginBottom={2}>
              Zugeordnete Organisation
            </Typography>
          </Grid>

          {user.organization ? (
            <>
              <Grid md={6}>
                <FormControl>
                  <FormLabel>Organisation</FormLabel>
                  <Input value={user.organization?.organization?.name} readOnly />
                </FormControl>
              </Grid>

              <Grid md={6}>
                <FormControl>
                  <FormLabel>Typ</FormLabel>
                  <Input
                    value={
                      user.organization?.organization && I18N_DE.OrganizationType[user.organization.organization.type]
                    }
                    readOnly
                  />
                </FormControl>
              </Grid>
              <Grid md={6}>
                <FormControl>
                  <FormLabel>Rolle</FormLabel>
                  <Input value={user.organization && I18N_DE.OrganizationUserRole[user.organization.role]} readOnly />
                </FormControl>
              </Grid>

              <Grid md={6}>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Input
                    value={
                      user.organization &&
                      I18N_DE.EntityActive[user.organization.active ? EntityActive.TRUE : EntityActive.FALSE]
                    }
                    readOnly
                  />
                </FormControl>
              </Grid>

              <Grid md={12}>
                <Stack direction="row" justifyContent="space-between">
                  <ButtonLink
                    to="/administration/organisationen/$organizationId"
                    params={{
                      organizationId: user.organization.organizationId,
                    }}
                    variant="outlined"
                  >
                    Organisation
                  </ButtonLink>
                  <ButtonLink
                    to="/administration/organisationen/$organizationId/benutzerinnen/$userId"
                    params={{
                      organizationId: user.organization.organizationId,
                      userId,
                    }}
                  >
                    Bearbeiten
                  </ButtonLink>
                </Stack>
              </Grid>
            </>
          ) : (
            <Grid md={12}>
              <Typography>
                Benutzer*in keiner Organisation zugewiesen. Jetzt{' '}
                <RouterLink to="/administration/benutzerinnen/$userId/organisation/hinzufuegen" params={{ userId }}>
                  zuordnen
                </RouterLink>
              </Typography>
            </Grid>
          )}
        </Grid>
      </Stack>
    )
  );
}
